import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCPopover, RCCTooltip } from "react-common-components";
import { useFieldArray } from "react-hook-form";
import CardContractItemType from "src/components/CardContractItemType";
import Loader from "src/components/Loader";
import LovUsersGroups from "src/components/Lov/UsersGroups";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { IComponentInviteUser } from "./interface";
import Label from "./styles";

const InviteUser: React.FC<IComponentInviteUser> = ({
	control,
	error,
	register,
	clearErrors,
	businessGroupId
}) => {
	const { addToast } = RCCHookToast();
	const { translate } = useTranslation();
	const { businessGroup } = useAuth();

	const [loading, setLoading] = useState(false);
	const [defaultValue, setDefaultValue] = useState<{value: string, label: string} | undefined>(undefined);
	const defaultValueEmails = useRef<string[]>([]);
	const { 
		fields, 
		remove,
		append
	} = useFieldArray({
		control,
		name: "invites",
		shouldUnregister: true
	});

	const addInvite = (email = "") => {
		clearErrors("invites");
		const invite: any = {};
		invite.key = v4();
		invite.efa026Email = email;
		defaultValueEmails.current = [
			...defaultValueEmails.current,
			email
		];
		append(invite);
	};

	const removeInvite = (index: number) => {	
		clearErrors("invites");		
		remove(index);
	};

	const importFile = (e: ChangeEvent<HTMLInputElement>) => {
		try{
			if(!e.target.files || e.target.files.length === 0)
				return;

			const reader = new FileReader();
			reader.onloadend = (file) => {
				if(!file.target)
					return;

				const result = file.target.result?.toString().split(/\r?\n/); // file.target.result?.toString().split(";");
				if(!result || result.length === 0)
					return;
					
				result.forEach((email: string) => {
					addInvite(email.trim());
				});
			};
			reader.readAsText(e.target.files[0]);
		}
		catch(err){
			standardErrorMessages(err, translate("Erro ao ler o arquivo"), addToast);
		}
	};

	useEffect(() => {
		addInvite();
	}, []);

	return (
		<RCCGrid
			container
			item
			direction="column"
			alignItems="center"
			justifyContent="center"
		>
			{businessGroup.efa001Id === businessGroupId && (
				<RCCGrid container item>
					<CardContractItemType 
						type="13"
					/>
				</RCCGrid>
			)}
			
			<br />
			
			<RCCGrid
				container
				item
			>
				<RCCGrid
					container
					item
					justifyContent="flex-end"
					alignItems="center"
					spacing={1}
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={4}
					>
						<LovUsersGroups 
							control={control}
							name="inviteGroups"
							label={`${translate("Grupo de usuários")} ${translate("padrão")}`}
							extraParametersOnLoad={{
								businessGroupId
							}}
							onChangeCapture={(e: {value: string, label: string}) => {
								setLoading(true);
								setDefaultValue(e);
								window.setTimeout(() => {
									setLoading(false);
								}, 200);
							}}
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						justifyContent="flex-end"
						xs={12}
						sm={2}
					>
						<RCCPopover
							color="grey"
							header={{
								title: translate("Importar usuários")
							}}
							body={{
								text: translate("Utilize arquivos no formato .txt ou .csv. O arquivo deve conter os e-mails dos usuários separados por uma quebra de linha")
							}}
							position="leftEnd"
						>
							<Label htmlFor="importFile">
								<RCCIcon name="documentTXT" />
								<input 
									type="file"
									id="importFile"
									accept="text/plain, .csv"
									style={{ display: "none" }}
									onChange={importFile}
								></input>
							</Label>
						</RCCPopover>
						<RCCTooltip
							color="purple"
							text={translate("Adicionar usuário")}
							position="leftEnd"
						>
							<RCCButton
								color="purple"
								icon={{
									name: "plus"
								}}
								layout="isRounded"
								size="md"
								onClick={() => addInvite()}
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
			</RCCGrid>
								
			<RCCGrid
				container
				item
			>
				{error.invites && error.invites.message && (
					<Title color="red" size="xs">
						<RCCIcon
							name="infoCircle"
							color="var(--red)"
							style={{ marginRight: "8px"}}
						/>
						{error.invites.message}
					</Title>
				)}
				{fields.length === 0 && (
					<RCCAlert
						color="grey"
						title={translate("Nenhum usuário adicionado")}
						description={translate("Aperte no botão acima para adicionar")}
					/>
				)}
				
				{loading ? <Loader /> : fields.map((miles: any, index) => (
					<RCCGrid
						container
						item
						alignItems="center"
						key={miles.key}
						spacing={1}
					>
						<RCCGrid
							container
							item
							justifyContent="center"
							xs={12}
							sm={6}
						>
							<RCCInput 
								register={register}
								name={`invites[${index}].efa026Email`}
								label="Email"
								maxLength={200}
								type="email"
								required
								onBlurCapture={(e) => {
									const defaultVal = [...defaultValueEmails.current];
									defaultVal[index] = e.target.value;
									defaultValueEmails.current = defaultVal;
								}}
								defaultValue={defaultValueEmails.current[index]}
								error={error.invites && error.invites[index]?.efa026Email}
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							justifyContent="center"
							xs={12}
							sm={4}
						>
							<LovUsersGroups 
								control={control}
								name={`invites[${index}].efa026Efa004Id`}
								label={translate("Grupo de usuários")}
								extraParametersOnLoad={{
									businessGroupId
								}}
								defaultValue={defaultValue}
								error={error.invites && error.invites[index]?.efa026Efa004Id}
								required
							/>
						</RCCGrid>
						<RCCGrid
							container
							item
							justifyContent="flex-end"
							xs={12}
							sm={2}
						>
							<RCCTooltip
								color="red"
								text={translate("Excluir")}
							>
								<RCCButton
									color="transparent"
									icon={{
										name: "trash",
										color: "var(--red)"
									}}
									layout="isRounded"
									onClick={() => removeInvite(index)}
									tabIndex={-1}
								/>
							</RCCTooltip>
						</RCCGrid>
					</RCCGrid>											
				))}
			</RCCGrid>
		</RCCGrid>
	);
};

export default InviteUser;
